import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { ChevronRight as ChevronRightIcon, PushPin as PushPinIcon } from '@mui/icons-material';
import { getColorForPath } from '../navigationUtils';

const NavigationBreadcrumbs = ({
  navigationHistory,
  pinnedItems,
  location,
  handleNavigation,
  handleTogglePin,
  navLinks
}) => {
  // Show navigation breadcrumb only if there are items to display
  if (pinnedItems.length === 0 && navigationHistory.length === 0) {
    return null;
  }

  // Show recent navigation history and pinned items (max 5 each)
  const maxItems = 5;
  const displayedRecentItems = navigationHistory
    .filter(item => !pinnedItems.some(pinned => pinned.path === item.path))
    .slice(0, Math.min(navigationHistory.length, maxItems));
  const displayedPinnedItems = pinnedItems.slice(0, Math.min(pinnedItems.length, maxItems));

  return (
    <>
      {/* Show recent navigation history if available */}
      {displayedRecentItems.length > 0 && (
        <>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary', 
            fontWeight: 500,
            minWidth: 'fit-content'
          }}>
            Recent:
          </Typography>
          {displayedRecentItems.map((item, index) => {
            const itemColor = getColorForPath(item.path, navLinks);
            const isCurrentPage = location.pathname === item.path;
            
            return (
              <React.Fragment key={`recent-${item.path}`}>
                <Paper
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: itemColor,
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: isCurrentPage ? 600 : 500,
                    minWidth: 'fit-content',
                    maxWidth: '120px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    opacity: isCurrentPage ? 1 : 0.9,
                    boxShadow: isCurrentPage ? '0 2px 4px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.1)',
                    position: 'relative',
                    border: '2px solid rgba(255,255,255,0.3)',
                    '&:hover': {
                      backgroundColor: itemColor,
                      opacity: 1,
                      filter: 'brightness(1.1)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 3px 6px rgba(0,0,0,0.2)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{
                      fontWeight: 'inherit',
                      fontSize: 'inherit',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flexGrow: 1
                    }}>
                      {item.label}
                    </Typography>
                    <PushPinIcon 
                      onClick={(e) => handleTogglePin(item, e)}
                      sx={{ 
                        fontSize: '12px',
                        opacity: 0.8,
                        cursor: 'pointer',
                        transform: 'rotate(0deg)',
                        '&:hover': {
                          opacity: 1,
                          transform: 'rotate(0deg) scale(1.1)'
                        }
                      }}
                    />
                  </Box>
                </Paper>
                {index < displayedRecentItems.length - 1 && (
                  <ChevronRightIcon sx={{
                    color: 'text.disabled',
                    fontSize: '16px',
                    flexShrink: 0
                  }} />
                )}
              </React.Fragment>
            );
          })}
        </>
      )}

      {/* Show pinned items if available */}
      {displayedPinnedItems.length > 0 && (
        <>
          {displayedRecentItems.length > 0 && (
            <Box sx={{ mx: 1, width: '1px', height: '20px', backgroundColor: 'divider' }} />
          )}
          <Typography variant="body2" sx={{ 
            color: 'text.secondary', 
            fontWeight: 500,
            minWidth: 'fit-content'
          }}>
            Pinned:
          </Typography>
          {displayedPinnedItems.map((item, index) => {
        const itemColor = getColorForPath(item.path, navLinks);
        const isCurrentPage = location.pathname === item.path;
        
        return (
          <React.Fragment key={`pinned-${item.path}`}>
            <Paper
              onClick={() => handleNavigation(item.path)}
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                backgroundColor: itemColor,
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: isCurrentPage ? 600 : 500,
                minWidth: 'fit-content',
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                opacity: isCurrentPage ? 1 : 0.9,
                boxShadow: isCurrentPage ? '0 2px 4px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.1)',
                position: 'relative',
                border: '2px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  backgroundColor: itemColor,
                  opacity: 1,
                  filter: 'brightness(1.1)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 3px 6px rgba(0,0,0,0.2)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'inherit',
                  fontSize: 'inherit',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexGrow: 1
                }}>
                  {item.label}
                </Typography>
                <PushPinIcon 
                  onClick={(e) => handleTogglePin(item, e)}
                  sx={{ 
                    fontSize: '12px',
                    opacity: 0.8,
                    cursor: 'pointer',
                    transform: 'rotate(45deg)',
                    '&:hover': {
                      opacity: 1,
                      transform: 'rotate(45deg) scale(1.1)'
                    }
                  }}
                />
              </Box>
            </Paper>
            {index < displayedPinnedItems.length - 1 && (
              <ChevronRightIcon sx={{ 
                color: 'text.disabled', 
                fontSize: '16px',
                flexShrink: 0
              }} />
            )}
          </React.Fragment>

          )
          })}
        </>
      )}
    </>
  );

};

export default NavigationBreadcrumbs; 